import { useCallback, useEffect, type MutableRefObject } from 'react';

import type { CalendarContextProps } from '../contexts/CalendarContext.js';
import {
  NUMBER_OF_MONTHS_PER_ROW,
  NUMBER_OF_WEEKDAYS,
  NUMBER_OF_YEARS,
  NUMBER_OF_YEARS_PER_ROW,
} from '../types/calendarConfiguration.js';
import type {
  CalendarViewType,
  CalendarNavigationPayload,
} from '../types/shared-types.js';

export interface UseCalendarNavigationProps
  extends Pick<CalendarContextProps, 'dispatch'> {
  calendarRef: MutableRefObject<HTMLElement | null>;
  view: CalendarViewType;
}

// Set of keys that should not prevent the default action
const allowedKeys = new Set(['Tab', 'Enter', 'NumpadEnter', ' ']);

// Set of keys for navigating the calendar body
// Pressing one of those keys should move the focus to the calendar body
const calendarNavigationKeyCodes = new Set([
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'ArrowLeft',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

const getMultiplier = (view: CalendarViewType) => {
  switch (view) {
    case 'day':
      return NUMBER_OF_WEEKDAYS;
    case 'month':
      return NUMBER_OF_MONTHS_PER_ROW;
    case 'year':
      return NUMBER_OF_YEARS_PER_ROW;
  }
};

const getPageUpDownMultiplier = (
  view: CalendarViewType,
  event: KeyboardEvent,
  modifier: 1 | -1,
): CalendarNavigationPayload => {
  if (view === 'day') {
    // Go to previous month. If ALT or SHIFT key is pressed go to previous year instead
    const unit = event.altKey || event.shiftKey ? 'year' : 'month';
    return { amount: 1 * modifier, unit };
  }

  if (view === 'month') {
    return { amount: 1 * modifier, unit: 'year' };
  }
  return { amount: NUMBER_OF_YEARS * modifier };
};

const getNavigationConfig = (
  view: CalendarViewType,
  event: KeyboardEvent,
): CalendarNavigationPayload | undefined => {
  const multiplier = getMultiplier(view);

  switch (event.key) {
    case 'ArrowUp':
      // Go to previous row
      return { amount: -1 * multiplier };
    case 'ArrowDown':
      // Go to next week
      return { amount: 1 * multiplier };
    case 'ArrowLeft':
      return { amount: -1 };
    case 'ArrowRight':
      return { amount: 1 };
    case 'PageUp':
      return getPageUpDownMultiplier(view, event, -1);
    case 'PageDown':
      return getPageUpDownMultiplier(view, event, 1);
    // For day view only
    case 'Home':
      // Go to the first day of the week.
      if (view === 'day') {
        return { amount: 'startOfWeek' };
      }
      break;
    case 'End':
      // Go to the last day of the week.
      if (view === 'day') {
        return { amount: 'endOfWeek' };
      }
      break;
  }
};

/** Custom hook for adding keyboard support to the calendar. */
export function useCalendarNavigation({
  calendarRef,
  view,
  dispatch,
}: UseCalendarNavigationProps) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!allowedKeys.has(event.key)) {
        event.preventDefault();
      }

      // Dispatch state change to actually focus the active calendar body element
      if (calendarNavigationKeyCodes.has(event.key)) {
        dispatch({ type: 'shouldUpdateFocusFromKeydown', payload: true });
      }

      const payload = getNavigationConfig(view, event);

      if (payload) {
        dispatch({ type: 'navigateCurrentView', payload });
      }
    },
    [dispatch, view],
  );

  // Register event listeners to listen for key events
  useEffect(() => {
    const calendarRefCurrent = calendarRef.current;
    calendarRefCurrent?.addEventListener('keydown', handleKeyDown);

    return () => {
      calendarRefCurrent?.removeEventListener('keydown', handleKeyDown);
    };
  }, [calendarRef, handleKeyDown]);
}
